import { useEffect, useState } from "react";
import { api, timeAgo } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit, Trash2, RefreshCw, Sparkles, ExternalLink, X, Eye } from "lucide-react";
import RichEditor from "@/components/RichEditor";

const EMPTY_SOURCE = {
  name: "",
  url: "",
  type: "rss",
  category_slug: "actualidad",
  active: true,
  item_selector: "",
  title_selector: "",
  link_attr: "href",
  base_url: "",
};

export function AdminSourcesTab({ categories }) {
  const [sources, setSources] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r = await api.get("/admin/sources");
    setSources(r.data || []);
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing.id) {
        await api.put(`/admin/sources/${editing.id}`, editing);
      } else {
        await api.post("/admin/sources", editing);
      }
      toast.success("Fuente guardada");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error guardando");
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`¿Eliminar fuente "${s.name}"?`)) return;
    await api.delete(`/admin/sources/${s.id}`);
    load();
  };

  const syncOne = async (s) => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/sources/${s.id}/sync`);
      toast.success(`${s.name}: ${r.data.new} notas nuevas (${r.data.total_seen} vistas)`);
      load();
    } catch (err) {
      toast.error(`Error: ${err.response?.data?.detail || err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const syncAll = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/sources/sync-all");
      const total = r.data.results.reduce((acc, x) => acc + (x.new || 0), 0);
      toast.success(`Sync completo: ${total} notas nuevas en ${r.data.results.length} fuentes`);
      load();
    } catch (err) {
      toast.error("Error en sync");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-sources-tab">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-headline uppercase text-2xl">Fuentes</h2>
          <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
            {sources.length} fuentes · RSS + HTML scraping
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={syncAll} disabled={busy} className="btn-ghost flex items-center gap-1 disabled:opacity-50" data-testid="sources-sync-all">
            <RefreshCw size={14} className={busy ? "animate-spin" : ""} /> Sync todas
          </button>
          <button onClick={() => setEditing({ ...EMPTY_SOURCE })} className="btn-accent" data-testid="sources-new">
            <Plus size={14} /> Nueva fuente
          </button>
        </div>
      </div>

      <div className="border rounded-sm overflow-hidden" style={{ borderColor: "var(--border)" }}>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
              <th className="text-left p-3 overline">Nombre</th>
              <th className="text-left p-3 overline">Tipo</th>
              <th className="text-left p-3 overline">Categoría</th>
              <th className="text-left p-3 overline">Último sync</th>
              <th className="text-left p-3 overline">Activa</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sources.map((s) => (
              <tr key={s.id} className="border-b" style={{ borderColor: "var(--border)" }}>
                <td className="p-3 font-semibold">{s.name}</td>
                <td className="p-3 uppercase text-xs accent-text">{s.type}</td>
                <td className="p-3 uppercase text-xs">{s.category_slug}</td>
                <td className="p-3 text-xs" style={{ color: "var(--text-muted)" }}>
                  {s.last_synced_at ? `${timeAgo(s.last_synced_at)} · +${s.last_count || 0}` : "—"}
                </td>
                <td className="p-3 text-xs">{s.active ? "Sí" : "No"}</td>
                <td className="p-3 flex gap-2 justify-end">
                  <button onClick={() => syncOne(s)} disabled={busy} className="p-2 hover:accent-text" title="Sincronizar"><RefreshCw size={14} className={busy ? "animate-spin" : ""} /></button>
                  <button onClick={() => setEditing(s)} className="p-2 hover:accent-text"><Edit size={14} /></button>
                  <button onClick={() => remove(s)} className="p-2 hover:text-red-600"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-10 px-4" style={{ background: "rgba(0,0,0,0.6)" }}>
          <form onSubmit={save} className="w-full max-w-2xl rounded-sm border max-h-[90vh] overflow-y-auto" style={{ background: "var(--bg)", borderColor: "var(--border)" }}>
            <div className="flex items-center justify-between p-5 border-b sticky top-0" style={{ borderColor: "var(--border)", background: "var(--bg)" }}>
              <h2 className="font-headline uppercase text-xl">{editing.id ? "Editar fuente" : "Nueva fuente"}</h2>
              <button type="button" onClick={() => setEditing(null)}><X size={20} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="overline block mb-1">Nombre *</label>
                  <input required className="w-full px-3 py-2 border rounded-sm bg-transparent" style={{ borderColor: "var(--border)" }} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
                </div>
                <div>
                  <label className="overline block mb-1">Tipo</label>
                  <select className="w-full px-3 py-2 border rounded-sm bg-transparent" style={{ borderColor: "var(--border)" }} value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })}>
                    <option value="rss">RSS feed</option>
                    <option value="html">HTML scraping</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="overline block mb-1">URL *</label>
                <input required className="w-full px-3 py-2 border rounded-sm bg-transparent" style={{ borderColor: "var(--border)" }} value={editing.url} onChange={(e) => setEditing({ ...editing, url: e.target.value })} placeholder={editing.type === "rss" ? "https://medio.com/rss" : "https://medio.com/seccion"} />
              </div>
              <div>
                <label className="overline block mb-1">Categoría destino</label>
                <select className="w-full px-3 py-2 border rounded-sm bg-transparent" style={{ borderColor: "var(--border)" }} value={editing.category_slug} onChange={(e) => setEditing({ ...editing, category_slug: e.target.value })}>
                  {categories.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select>
              </div>
              {editing.type === "html" && (
                <div className="border rounded-sm p-4 space-y-3" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
                  <p className="overline">Selectores CSS para HTML scraping</p>
                  <div>
                    <label className="text-xs block mb-1" style={{ color: "var(--text-muted)" }}>Selector items (ej: <code>article h2 a</code>)</label>
                    <input className="w-full px-3 py-2 border rounded-sm bg-transparent" style={{ borderColor: "var(--border)" }} value={editing.item_selector || ""} onChange={(e) => setEditing({ ...editing, item_selector: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-xs block mb-1" style={{ color: "var(--text-muted)" }}>Selector título (opcional, dentro de item)</label>
                    <input className="w-full px-3 py-2 border rounded-sm bg-transparent" style={{ borderColor: "var(--border)" }} value={editing.title_selector || ""} onChange={(e) => setEditing({ ...editing, title_selector: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-xs block mb-1" style={{ color: "var(--text-muted)" }}>Base URL (para links relativos)</label>
                    <input className="w-full px-3 py-2 border rounded-sm bg-transparent" style={{ borderColor: "var(--border)" }} value={editing.base_url || ""} onChange={(e) => setEditing({ ...editing, base_url: e.target.value })} placeholder="https://medio.com" />
                  </div>
                </div>
              )}
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} />
                Activa (incluir en sync-all)
              </label>
              <div className="flex gap-2 justify-end pt-3 border-t" style={{ borderColor: "var(--border)" }}>
                <button type="button" className="btn-ghost" onClick={() => setEditing(null)}>Cancelar</button>
                <button type="submit" className="btn-accent">Guardar</button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

export function AdminCapturedTab({ categories, onPromote }) {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("captured");
  const [busy, setBusy] = useState(false);
  const [editingImg, setEditingImg] = useState(null);
  const [editing, setEditing] = useState(null);
  const [publishing, setPublishing] = useState(null);

  const load = async () => {
    const r = await api.get("/admin/captured", { params: { status_filter: status, limit: 200 } });
    setItems(r.data || []);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const discard = async (item) => {
    await api.put(`/admin/captured/${item.id}/status`, { status: "discarded" });
    load();
  };

  const saveEdit = async () => {
    try {
      await api.put(`/admin/captured/${editing.id}`, {
        title: editing.title,
        summary: editing.summary,
        image: editing.image,
        image_caption: editing.image_caption,
        image_credit: editing.image_credit,
        body_html: editing.body_html,
        category_slug: editing.category_slug,
      });
      toast.success("Captura actualizada");
      setEditing(null);
      load();
    } catch (err) {
      toast.error("Error guardando");
    }
  };

  const enrichCapture = async () => {
    if (!editing) return;
    try {
      setBusy(true);
      const r = await api.post(`/admin/captured/${editing.id}/enrich`);
      setEditing({ ...editing, ...r.data });
      toast.success("Artículo enriquecido");
    } catch (err) {
      toast.error("No se pudo enriquecer");
    } finally {
      setBusy(false);
    }
  };

  const aiSummarize = async () => {
    if (!editing) return;
    try {
      setBusy(true);
      const r = await api.post(`/admin/captured/${editing.id}/summarize`);
      if (r.data?.summary) {
        setEditing({ ...editing, summary: r.data.summary });
        toast.success("Resumen generado");
      } else {
        toast.error(r.data?.error || "Sin contenido");
      }
    } catch (err) {
      toast.error("Error al resumir");
    } finally {
      setBusy(false);
    }
  };

  const saveImage = async () => {
    if (!editingImg) return;
    await api.put(`/admin/captured/${editingImg.id}`, { image: editingImg.image });
    toast.success("Imagen actualizada");
    setEditingImg(null);
    load();
  };

  const publishDirect = async () => {
    if (!publishing) return;
    setBusy(true);
    try {
      await api.post(`/admin/captured/${publishing.id}/publish-direct`, {
        title: publishing.title,
        bajada: publishing.summary?.replace(/<[^>]+>/g, "").slice(0, 280),
        body: `<p>${(publishing.summary || "").replace(/<[^>]+>/g, "")}</p><p><a href="${publishing.link}" target="_blank" rel="noreferrer">Leé la nota original en ${publishing.source_name}</a></p>`,
        category_slug: publishing.category_slug,
        featured_image: publishing.image,
        highlight_level: publishing.highlight_level || "normal",
        is_breaking: !!publishing.is_breaking,
      });
      toast.success("Publicada");
      setPublishing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error publicando");
    } finally {
      setBusy(false);
    }
  };

  const promote = async (item) => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/captured/${item.id}/fetch-content`);
      onPromote({
        raw_text: r.data.text,
        source_name: r.data.source_name,
        source_url: r.data.link,
        category_slug: r.data.category_slug,
        featured_image: r.data.image,
        title_hint: r.data.title,
        item_id: item.id,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error obteniendo contenido");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-captured-tab">
      <div className="flex items-center justify-between mb-5 gap-4 flex-wrap">
        <div>
          <h2 className="font-headline uppercase text-2xl">Captura</h2>
          <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
            {items.length} {status === "captured" ? "pendientes" : status === "promoted" ? "publicadas" : "descartadas"}
          </p>
        </div>
        <div className="flex gap-1 border rounded-sm overflow-hidden text-xs uppercase tracking-widest font-bold" style={{ borderColor: "var(--border)" }}>
          {["captured", "promoted", "discarded"].map(s => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className="px-3 py-2"
              style={{
                background: status === s ? "var(--accent)" : "transparent",
                color: status === s ? "#fff" : "var(--text-muted)",
              }}
              data-testid={`captured-filter-${s}`}
            >
              {s === "captured" ? "Pendientes" : s === "promoted" ? "Publicadas" : "Descartadas"}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((it) => (
          <article key={it.id} className="rounded-sm border p-4 flex gap-3" style={{ borderColor: "var(--border)" }} data-testid={`captured-card-${it.id}`}>
            <div className="relative shrink-0">
              {it.image ? (
                <img src={it.image} alt="" className="w-24 h-24 object-cover rounded-sm" onError={(e) => { e.target.style.opacity = "0.3"; }} />
              ) : (
                <div className="w-24 h-24 rounded-sm" style={{ background: "var(--surface-2)" }} />
              )}
              <button
                onClick={() => setEditingImg({ id: it.id, image: it.image || "" })}
                className="absolute bottom-1 right-1 w-6 h-6 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-black"
                title="Cambiar imagen"
                data-testid={`captured-edit-img-${it.id}`}
              >
                <Edit size={11} />
              </button>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 overline mb-1 flex-wrap">
                <span className="accent-text">{it.source_name}</span>
                <span style={{ color: "var(--text-muted)" }}>·</span>
                <span style={{ color: "var(--text-muted)" }}>{it.category_slug?.toUpperCase()}</span>
                <span style={{ color: "var(--text-muted)" }}>·</span>
                <span style={{ color: "var(--text-muted)" }}>{timeAgo(it.captured_at)}</span>
              </div>
              <h3 className="font-bold text-sm leading-tight mb-2 line-clamp-3">{it.title}</h3>
              {it.summary && (
                <p className="text-xs mb-2 line-clamp-2" style={{ color: "var(--text-muted)" }}>{it.summary.replace(/<[^>]+>/g, "")}</p>
              )}
              <div className="flex items-center gap-2 flex-wrap">
                <a href={it.link} target="_blank" rel="noreferrer" className="text-xs accent-text hover:underline flex items-center gap-1">
                  <ExternalLink size={11} /> Original
                </a>
                {status === "captured" && (
                  <>
                    <button onClick={() => setPublishing(it)} className="text-xs font-bold uppercase tracking-widest accent-text hover:underline" data-testid={`publish-direct-${it.id}`}>
                      Publicar
                    </button>
                    <button onClick={() => setEditing({ ...it })} className="text-xs font-bold uppercase tracking-widest hover:underline" style={{ color: "var(--text)" }} data-testid={`edit-captured-${it.id}`}>
                      Editar
                    </button>
                    <button onClick={() => promote(it)} disabled={busy} className="text-xs font-bold uppercase tracking-widest accent-text hover:underline disabled:opacity-50 flex items-center gap-1" data-testid={`promote-${it.id}`}>
                      <Sparkles size={11} /> IA
                    </button>
                    <button onClick={() => discard(it)} className="text-xs uppercase tracking-widest hover:text-red-600" style={{ color: "var(--text-muted)" }} data-testid={`discard-${it.id}`}>
                      Descartar
                    </button>
                  </>
                )}
              </div>
            </div>
          </article>
        ))}
      </div>
      {items.length === 0 && (
        <div className="py-12 text-center" style={{ color: "var(--text-muted)" }}>
          No hay items {status === "captured" ? "pendientes" : status === "promoted" ? "publicados" : "descartados"}. Andá a <strong>Fuentes</strong> y hacé &ldquo;Sync todas&rdquo;.
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-6 px-4 overflow-y-auto" style={{ background: "rgba(0,0,0,0.6)" }}>
          <div className="w-full max-w-3xl rounded-sm border p-5 my-auto" style={{ background: "var(--bg)", borderColor: "var(--border)" }} data-testid="edit-captured-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-headline uppercase text-lg">Editar captura</h3>
              <div className="flex gap-2">
                <button onClick={enrichCapture} disabled={busy} className="btn-ghost text-xs disabled:opacity-50" data-testid="edit-enrich" title="Re-fetch full content from source URL">
                  {busy ? "..." : "↻ Enriquecer"}
                </button>
                <button onClick={aiSummarize} disabled={busy} className="btn-ghost text-xs disabled:opacity-50" data-testid="edit-ai-summary" title="Generar resumen con IA">
                  {busy ? "..." : "✨ Resumir IA"}
                </button>
              </div>
            </div>
            <label className="overline block mb-1">Título</label>
            <input value={editing.title || ""} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="w-full px-3 py-2 border rounded-sm bg-transparent mb-3" style={{ borderColor: "var(--border)" }} data-testid="edit-captured-title" />
            <label className="overline block mb-1">Resumen (corto)</label>
            <textarea value={(editing.summary || "").replace(/<[^>]+>/g, "")} onChange={(e) => setEditing({ ...editing, summary: e.target.value })} className="w-full px-3 py-2 border rounded-sm bg-transparent mb-3 min-h-[80px]" style={{ borderColor: "var(--border)" }} />
            <label className="overline block mb-1">Imagen principal (URL)</label>
            <input value={editing.image || ""} onChange={(e) => setEditing({ ...editing, image: e.target.value })} placeholder="https://..." className="w-full px-3 py-2 border rounded-sm bg-transparent mb-2" style={{ borderColor: "var(--border)" }} />
            {editing.image && <img src={editing.image} alt="" className="w-full aspect-video object-cover rounded-sm mb-3" onError={(e) => { e.target.style.opacity = "0.3"; }} />}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <div>
                <label className="overline block mb-1">Leyenda de la foto</label>
                <input value={editing.image_caption || ""} onChange={(e) => setEditing({ ...editing, image_caption: e.target.value })} placeholder="Texto al pie de la imagen" className="w-full px-3 py-2 border rounded-sm bg-transparent" style={{ borderColor: "var(--border)" }} />
              </div>
              <div>
                <label className="overline block mb-1">Crédito / Fuente foto</label>
                <input value={editing.image_credit || ""} onChange={(e) => setEditing({ ...editing, image_credit: e.target.value })} placeholder="Foto: AFP / X" className="w-full px-3 py-2 border rounded-sm bg-transparent" style={{ borderColor: "var(--border)" }} />
              </div>
            </div>
            <label className="overline block mb-1">Cuerpo completo del artículo</label>
            <div className="mb-3 border rounded-sm" style={{ borderColor: "var(--border)" }}>
              <RichEditor value={editing.body_html || ""} onChange={(html) => setEditing({ ...editing, body_html: html })} />
            </div>
            {editing.embeds && editing.embeds.length > 0 && (
              <div className="mb-3 p-3 border rounded-sm" style={{ borderColor: "var(--border)", background: "var(--surface-2)" }}>
                <p className="overline mb-2">Embeds detectados ({editing.embeds.length})</p>
                <div className="text-xs space-y-1 max-h-32 overflow-y-auto" style={{ color: "var(--text-muted)" }}>
                  {editing.embeds.map((em, i) => (
                    <div key={i} className="font-mono truncate">
                      [{em.type}{em.provider ? ` · ${em.provider}` : ""}] {em.src}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <label className="overline block mb-1">Categoría</label>
            <select value={editing.category_slug || "actualidad"} onChange={(e) => setEditing({ ...editing, category_slug: e.target.value })} className="w-full px-3 py-2 border rounded-sm bg-transparent mb-3" style={{ borderColor: "var(--border)" }}>
              {categories.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
            <div className="flex gap-2 justify-end pt-3 border-t" style={{ borderColor: "var(--border)" }}>
              <button onClick={() => setEditing(null)} className="btn-ghost">Cancelar</button>
              <button onClick={saveEdit} className="btn-accent" data-testid="edit-captured-save">Guardar cambios</button>
            </div>
          </div>
        </div>
      )}

      {editingImg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(0,0,0,0.6)" }}>
          <div className="w-full max-w-md rounded-sm border p-5" style={{ background: "var(--bg)", borderColor: "var(--border)" }}>
            <h3 className="font-headline uppercase text-lg mb-3">Cambiar imagen</h3>
            <input
              value={editingImg.image}
              onChange={(e) => setEditingImg({ ...editingImg, image: e.target.value })}
              placeholder="https://..."
              className="w-full px-3 py-2 border rounded-sm bg-transparent mb-3"
              style={{ borderColor: "var(--border)" }}
              data-testid="captured-img-input"
            />
            {editingImg.image && <img src={editingImg.image} alt="" className="w-full aspect-video object-cover rounded-sm mb-3" />}
            <div className="flex gap-2 justify-end">
              <button onClick={() => setEditingImg(null)} className="btn-ghost">Cancelar</button>
              <button onClick={saveImage} className="btn-accent" data-testid="captured-img-save">Guardar</button>
            </div>
          </div>
        </div>
      )}

      {publishing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-10 px-4 overflow-y-auto" style={{ background: "rgba(0,0,0,0.6)" }}>
          <div className="w-full max-w-2xl rounded-sm border p-5 my-auto" style={{ background: "var(--bg)", borderColor: "var(--border)" }}>
            <h3 className="font-headline uppercase text-lg mb-3">Publicar nota</h3>
            <p className="text-xs mb-4" style={{ color: "var(--text-muted)" }}>Revisá y editá antes de publicar. Sin IA.</p>
            <label className="overline block mb-1">Título</label>
            <input
              value={publishing.title}
              onChange={(e) => setPublishing({ ...publishing, title: e.target.value })}
              className="w-full px-3 py-2 border rounded-sm bg-transparent mb-3"
              style={{ borderColor: "var(--border)" }}
            />
            <label className="overline block mb-1">Resumen</label>
            <textarea
              value={(publishing.summary || "").replace(/<[^>]+>/g, "")}
              onChange={(e) => setPublishing({ ...publishing, summary: e.target.value })}
              className="w-full px-3 py-2 border rounded-sm bg-transparent mb-3 min-h-[100px]"
              style={{ borderColor: "var(--border)" }}
            />
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="overline block mb-1">Categoría</label>
                <select
                  value={publishing.category_slug}
                  onChange={(e) => setPublishing({ ...publishing, category_slug: e.target.value })}
                  className="w-full px-3 py-2 border rounded-sm bg-transparent"
                  style={{ borderColor: "var(--border)" }}
                >
                  {categories.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="overline block mb-1">Highlight</label>
                <select
                  value={publishing.highlight_level || "normal"}
                  onChange={(e) => setPublishing({ ...publishing, highlight_level: e.target.value })}
                  className="w-full px-3 py-2 border rounded-sm bg-transparent"
                  style={{ borderColor: "var(--border)" }}
                >
                  <option value="normal">Normal</option>
                  <option value="featured">Destacada</option>
                  <option value="hero">Hero</option>
                </select>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm mb-4">
              <input type="checkbox" checked={!!publishing.is_breaking} onChange={(e) => setPublishing({ ...publishing, is_breaking: e.target.checked })} />
              ⚡ ÚLTIMO MOMENTO (banner rojo arriba del home)
            </label>
            <div className="flex gap-2 justify-end pt-3 border-t" style={{ borderColor: "var(--border)" }}>
              <button onClick={() => setPublishing(null)} className="btn-ghost">Cancelar</button>
              <button onClick={publishDirect} disabled={busy} className="btn-accent disabled:opacity-50" data-testid="publish-direct-confirm">
                {busy ? "Publicando..." : "Publicar ahora"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminPagesTab() {
  const [pages, setPages] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const r = await api.get("/admin/pages");
    setPages(r.data || []);
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing._existing) {
        await api.put(`/admin/pages/${editing.slug}`, editing);
      } else {
        await api.post("/admin/pages", editing);
      }
      toast.success("Página guardada");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error guardando");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.title}"?`)) return;
    await api.delete(`/admin/pages/${p.slug}`);
    load();
  };

  return (
    <div data-testid="admin-pages-tab">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-headline uppercase text-2xl">Páginas</h2>
          <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
            Términos, privacidad, nosotros, anunciantes y cualquier página estática
          </p>
        </div>
        <button onClick={() => setEditing({ slug: "", title: "", body: "", is_published: true, _existing: false })} className="btn-accent" data-testid="pages-new">
          <Plus size={14} /> Nueva página
        </button>
      </div>

      <div className="border rounded-sm overflow-hidden" style={{ borderColor: "var(--border)" }}>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
              <th className="text-left p-3 overline">Título</th>
              <th className="text-left p-3 overline">Slug</th>
              <th className="text-left p-3 overline">Publicada</th>
              <th className="text-left p-3 overline">Actualizada</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {pages.map((p) => (
              <tr key={p.id} className="border-b" style={{ borderColor: "var(--border)" }}>
                <td className="p-3 font-semibold">{p.title}</td>
                <td className="p-3 text-xs uppercase accent-text">/{p.slug}</td>
                <td className="p-3 text-xs">{p.is_published ? "Sí" : "No"}</td>
                <td className="p-3 text-xs" style={{ color: "var(--text-muted)" }}>{timeAgo(p.updated_at)}</td>
                <td className="p-3 flex gap-2 justify-end">
                  <a href={`/${p.slug}`} target="_blank" rel="noreferrer" className="p-2 hover:accent-text"><Eye size={14} /></a>
                  <button onClick={() => setEditing({ ...p, _existing: true })} className="p-2 hover:accent-text"><Edit size={14} /></button>
                  <button onClick={() => remove(p)} className="p-2 hover:text-red-600"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-10 px-4" style={{ background: "rgba(0,0,0,0.6)" }}>
          <form onSubmit={save} className="w-full max-w-3xl rounded-sm border max-h-[90vh] overflow-y-auto" style={{ background: "var(--bg)", borderColor: "var(--border)" }}>
            <div className="flex items-center justify-between p-5 border-b sticky top-0" style={{ borderColor: "var(--border)", background: "var(--bg)" }}>
              <h2 className="font-headline uppercase text-xl">{editing._existing ? `Editar: ${editing.title}` : "Nueva página"}</h2>
              <button type="button" onClick={() => setEditing(null)}><X size={20} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="overline block mb-1">Título *</label>
                  <input required className="w-full px-3 py-2 border rounded-sm bg-transparent" style={{ borderColor: "var(--border)" }} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="page-form-title" />
                </div>
                <div>
                  <label className="overline block mb-1">Slug * (URL)</label>
                  <input
                    required
                    disabled={editing._existing}
                    className="w-full px-3 py-2 border rounded-sm bg-transparent disabled:opacity-60"
                    style={{ borderColor: "var(--border)" }}
                    value={editing.slug}
                    onChange={(e) => setEditing({ ...editing, slug: e.target.value.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "") })}
                    data-testid="page-form-slug"
                  />
                </div>
              </div>
              <div>
                <label className="overline block mb-1">Contenido</label>
                <RichEditor
                  value={editing.body || ""}
                  onChange={(html) => setEditing({ ...editing, body: html })}
                  placeholder="Escribí el contenido de la página..."
                  minHeight={300}
                />
                <p className="text-xs mt-1.5" style={{ color: "var(--text-muted)" }}>
                  Editor visual con negrita, listas, links, imágenes, citas y más.
                </p>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editing.is_published} onChange={(e) => setEditing({ ...editing, is_published: e.target.checked })} />
                Publicada (visible al público)
              </label>
              <div className="flex gap-2 justify-end pt-3 border-t" style={{ borderColor: "var(--border)" }}>
                <button type="button" className="btn-ghost" onClick={() => setEditing(null)}>Cancelar</button>
                <button type="submit" className="btn-accent" data-testid="page-form-submit">Guardar página</button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

export function AdminContactTab() {
  const [messages, setMessages] = useState([]);
  const [open, setOpen] = useState(null);

  const load = async () => {
    const r = await api.get("/admin/contact");
    setMessages(r.data || []);
  };

  useEffect(() => { load(); }, []);

  const markRead = async (m) => {
    await api.put(`/admin/contact/${m.id}/read`);
    load();
  };

  const remove = async (m) => {
    if (!window.confirm("¿Eliminar mensaje?")) return;
    await api.delete(`/admin/contact/${m.id}`);
    setOpen(null);
    load();
  };

  const unread = messages.filter(m => !m.is_read).length;

  return (
    <div data-testid="admin-contact-tab">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-headline uppercase text-2xl">Mensajes</h2>
          <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
            {messages.length} en total · {unread} sin leer
          </p>
        </div>
      </div>

      <div className="border rounded-sm overflow-hidden" style={{ borderColor: "var(--border)" }}>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
              <th className="text-left p-3 overline">De</th>
              <th className="text-left p-3 overline">Asunto</th>
              <th className="text-left p-3 overline">Tema</th>
              <th className="text-left p-3 overline">Fecha</th>
              <th className="text-left p-3 overline">Estado</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {messages.map((m) => (
              <tr key={m.id} className="border-b cursor-pointer hover:bg-[color:var(--surface)]" style={{ borderColor: "var(--border)", fontWeight: m.is_read ? 400 : 700 }} onClick={() => { setOpen(m); if (!m.is_read) markRead(m); }} data-testid={`message-row-${m.id}`}>
                <td className="p-3">{m.name} <span className="text-xs font-normal" style={{ color: "var(--text-muted)" }}>{m.email}</span></td>
                <td className="p-3">{m.subject}</td>
                <td className="p-3 text-xs uppercase accent-text">{m.topic}</td>
                <td className="p-3 text-xs" style={{ color: "var(--text-muted)" }}>{timeAgo(m.created_at)}</td>
                <td className="p-3 text-xs">
                  {!m.is_read && <span className="accent-bg text-white px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-widest">Nuevo</span>}
                  {m.email_sent && <span className="ml-1 text-xs" style={{ color: "var(--text-muted)" }}>· enviado</span>}
                </td>
                <td className="p-3"></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {messages.length === 0 && (
        <div className="py-12 text-center" style={{ color: "var(--text-muted)" }}>
          Aún no recibiste mensajes desde el formulario de contacto.
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-10 px-4" style={{ background: "rgba(0,0,0,0.6)" }}>
          <div className="w-full max-w-2xl rounded-sm border max-h-[90vh] overflow-y-auto" style={{ background: "var(--bg)", borderColor: "var(--border)" }}>
            <div className="flex items-center justify-between p-5 border-b sticky top-0" style={{ borderColor: "var(--border)", background: "var(--bg)" }}>
              <h2 className="font-headline uppercase text-xl">{open.subject}</h2>
              <button onClick={() => setOpen(null)}><X size={20} /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="flex flex-wrap gap-3 text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>
                <span><strong style={{ color: "var(--text)" }}>{open.name}</strong> · {open.email}</span>
                <span>·</span>
                <span className="accent-text">{open.topic}</span>
                <span>·</span>
                <span>{timeAgo(open.created_at)}</span>
              </div>
              <div className="border-t pt-4 whitespace-pre-wrap" style={{ borderColor: "var(--border)", color: "var(--text)" }}>
                {open.message}
              </div>
              <div className="flex gap-2 justify-end pt-3 border-t" style={{ borderColor: "var(--border)" }}>
                <a href={`mailto:${open.email}?subject=Re: ${encodeURIComponent(open.subject)}`} className="btn-accent">Responder por email</a>
                <button onClick={() => remove(open)} className="btn-ghost text-red-600 border-red-600">Eliminar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
